/**
 * PayMonthlyProviders Component
 * Displays finance providers for Sports Equipment & Cycling Equipment
 * Similar to FinanceThisCar but tailored for sports/cycling products
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Separator } from '../ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useCountry } from '../../context/CountryContext';
import { useToast } from '../../hooks/use-toast';
import {
  Banknote,
  Calculator,
  Building2,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Star,
  Shield,
  Clock,
  Percent,
  CreditCard,
  Receipt,
  Zap,
  RefreshCw,
  Info,
  Send,
  BadgeCheck
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge configurations
const BADGE_ICONS = {
  low_interest: Percent,
  quick_approval: Zap,
  flexible_terms: RefreshCw,
  trusted_lender: Building2,
  high_value: CreditCard
};

const BADGE_COLORS = {
  low_interest: 'bg-green-100 text-green-800 border-green-200',
  quick_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  flexible_terms: 'bg-blue-100 text-blue-800 border-blue-200',
  trusted_lender: 'bg-purple-100 text-purple-800 border-purple-200',
  high_value: 'bg-indigo-100 text-indigo-800 border-indigo-200'
};

const PayMonthlyProviders = ({ listing }) => {
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState('calculator');
  const [calculating, setCalculating] = useState(false);
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Calculator inputs
  const [calcInputs, setCalcInputs] = useState({
    deposit: 0,
    tenure_months: 12
  });
  
  // Application form
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    employment_type: 'salaried',
    monthly_income: ''
  });

  const itemPrice = parseFloat(listing?.price) || 0;
  const subcategory = listing?.subcategory || listing?.category || '';
  
  // Check if pay monthly is applicable
  const isApplicable = ['sports-equipment', 'cycling-equipment'].includes(subcategory);

  // Fetch providers
  useEffect(() => {
    const fetchProviders = async () => {
      if (!isApplicable || !itemPrice) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/sports-services/pay-monthly/providers`, {
          params: {
            country,
            subcategory,
            item_price: itemPrice
          }
        });
        
        if (response.data.success) {
          setProviders(response.data.providers);
          setConfig(response.data.config);
        }
      } catch (error) {
        console.error('Error fetching pay monthly providers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviders();
  }, [country, subcategory, itemPrice, isApplicable]);

  // Calculate quote
  const calculateQuote = useCallback(async () => {
    if (!selectedProvider || !itemPrice) return;
    
    try {
      setCalculating(true);
      const response = await axios.post(`${API_URL}/api/sports-services/pay-monthly/calculate`, null, {
        params: {
          provider_id: selectedProvider.id,
          item_price: itemPrice,
          deposit: calcInputs.deposit,
          tenure_months: calcInputs.tenure_months,
          country
        }
      });
      
      if (response.data.success) {
        setCalculatedQuote(response.data.calculation);
      } else {
        toast({
          title: 'Calculation Error',
          description: response.data.errors?.join(', ') || 'Could not calculate quote',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error calculating quote:', error);
    } finally {
      setCalculating(false);
    }
  }, [selectedProvider, itemPrice, calcInputs, country, toast]);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (showDialog && selectedProvider) {
      calculateQuote();
    }
  }, [showDialog, selectedProvider, calcInputs, calculateQuote]);

  // Submit application
  const handleSubmitApplication = async () => {
    if (!applicationForm.name || !applicationForm.email || !applicationForm.phone) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/sports-services/pay-monthly/apply`, {
        provider_id: selectedProvider.id,
        listing_id: listing.id || listing._id,
        item_price: itemPrice,
        deposit_amount: calcInputs.deposit,
        tenure_months: calcInputs.tenure_months,
        ...applicationForm,
        monthly_income: parseFloat(applicationForm.monthly_income) || null
      });
      
      if (response.data.success) {
        toast({
          title: 'Application Submitted!',
          description: `Reference: ${response.data.application_id}`,
        });
        setShowDialog(false);
        setDialogTab('calculator');
        setApplicationForm({
          name: '',
          email: '',
          phone: '',
          employment_type: 'salaried',
          monthly_income: ''
        });
      }
    } catch (error) {
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Open dialog for a provider
  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    setCalcInputs({
      deposit: provider.min_deposit_amount || 0,
      tenure_months: provider.max_tenure || 12
    });
    setShowDialog(true);
  };

  if (!isApplicable) return null;
  
  if (loading) {
    return (
      <Card className="border-2 border-dashed border-gray-200">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
          <span className="text-gray-500">Loading finance options...</span>
        </CardContent>
      </Card>
    );
  }
  
  if (providers.length === 0) return null;

  const currencySymbol = country === 'SL' ? 'Rs' : '£';

  return (
    <>
      <Card className="border-2 border-blue-100 shadow-sm" data-testid="pay-monthly-section">
        <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-gray-800">Pay Monthly</CardTitle>
                <p className="text-sm text-gray-500">
                  Split the cost with flexible payment plans
                </p>
              </div>
            </div>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              {providers.length} Provider{providers.length > 1 ? 's' : ''}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-4 space-y-3">
          {/* Quick Info Banner */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 text-sm">
              Finance from <span className="font-bold">{currencySymbol} {Math.min(...providers.map(p => p.estimated_monthly || 0)).toLocaleString()}/month</span> with approved credit
            </AlertDescription>
          </Alert>
          
          {/* Provider Cards */}
          <div className="space-y-3">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className="p-4 border rounded-lg hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer"
                onClick={() => handleSelectProvider(provider)}
                data-testid={`pay-monthly-provider-${provider.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Provider Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                          {provider.is_verified && (
                            <BadgeCheck className="h-4 w-4 text-blue-500" />
                          )}
                          {provider.is_sponsored && (
                            <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                              Featured
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                          <span>{provider.rating}</span>
                          <span className="text-gray-400">({provider.review_count?.toLocaleString()} reviews)</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Badges */}
                    {provider.badges && provider.badges.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {provider.badges.slice(0, 3).map((badge, idx) => {
                          const IconComponent = BADGE_ICONS[badge.id] || Shield;
                          const colorClass = BADGE_COLORS[badge.id] || 'bg-gray-100 text-gray-700';
                          return (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className={`text-xs ${colorClass}`}
                            >
                              <IconComponent className="h-3 w-3 mr-1" />
                              {badge.label}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Rate Info */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">
                        From <span className="font-bold text-green-600">{provider.min_rate}%</span> APR
                      </span>
                      <span className="text-gray-500">
                        {provider.min_tenure}-{provider.max_tenure} months
                      </span>
                      <span className="text-gray-500 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {provider.approval_time}
                      </span>
                    </div>
                  </div>
                  
                  {/* Monthly Payment & CTA */}
                  <div className="flex flex-col items-end gap-2">
                    {provider.estimated_monthly && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">From</p>
                        <p className="text-xl font-bold text-blue-600">
                          {currencySymbol} {provider.estimated_monthly.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">/month</p>
                      </div>
                    )}
                    <Button 
                      size="sm" 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid={`apply-pay-monthly-${provider.id}`}
                    >
                      Calculate <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Disclaimer */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Representative APR. Subject to status and affordability. Terms apply.
          </p>
        </CardContent>
      </Card>
      
      {/* Calculator & Application Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Finance Calculator
            </DialogTitle>
            <DialogDescription>
              {selectedProvider?.name} - Calculate your monthly payments
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={dialogTab} onValueChange={setDialogTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="calculator">
                <Calculator className="h-4 w-4 mr-2" />
                Calculator
              </TabsTrigger>
              <TabsTrigger value="apply">
                <Send className="h-4 w-4 mr-2" />
                Apply Now
              </TabsTrigger>
            </TabsList>
            
            {/* Calculator Tab */}
            <TabsContent value="calculator" className="space-y-4 mt-4">
              {/* Item Price Display */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Item Price</span>
                  <span className="text-xl font-bold text-gray-900">
                    {currencySymbol} {itemPrice.toLocaleString()}
                  </span>
                </div>
              </div>
              
              {/* Deposit Slider */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Deposit</Label>
                  <span className="text-sm font-medium">
                    {currencySymbol} {calcInputs.deposit.toLocaleString()} ({((calcInputs.deposit / itemPrice) * 100).toFixed(0)}%)
                  </span>
                </div>
                <Slider
                  value={[calcInputs.deposit]}
                  onValueChange={(v) => setCalcInputs(prev => ({ ...prev, deposit: v[0] }))}
                  min={selectedProvider?.min_deposit_amount || 0}
                  max={itemPrice * 0.5}
                  step={1000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Min: {currencySymbol} {(selectedProvider?.min_deposit_amount || 0).toLocaleString()}</span>
                  <span>Max: {currencySymbol} {(itemPrice * 0.5).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Tenure Selection */}
              <div className="space-y-2">
                <Label>Payment Term</Label>
                <Select
                  value={calcInputs.tenure_months.toString()}
                  onValueChange={(v) => setCalcInputs(prev => ({ ...prev, tenure_months: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenure" />
                  </SelectTrigger>
                  <SelectContent>
                    {[6, 12, 18, 24, 36, 48].filter(t => 
                      t >= (selectedProvider?.min_tenure || 3) && 
                      t <= (selectedProvider?.max_tenure || 48)
                    ).map(months => (
                      <SelectItem key={months} value={months.toString()}>
                        {months} months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Calculation Results */}
              {calculating ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : calculatedQuote && (
                <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Monthly Payment</span>
                    <span className="text-2xl font-bold text-blue-600">
                      {currencySymbol} {calculatedQuote.monthly_payment.toLocaleString()}
                    </span>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Borrow Amount</span>
                      <span>{currencySymbol} {calculatedQuote.borrow_amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">APR</span>
                      <span>{calculatedQuote.apr}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Interest</span>
                      <span>{currencySymbol} {calculatedQuote.total_interest.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Payable</span>
                      <span className="font-medium">{currencySymbol} {calculatedQuote.total_payable.toLocaleString()}</span>
                    </div>
                  </div>
                  {calculatedQuote.processing_fee > 0 && (
                    <p className="text-xs text-gray-500">
                      Processing fee: {currencySymbol} {calculatedQuote.processing_fee.toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              
              {/* Representative Example */}
              {calculatedQuote?.representative_example && (
                <p className="text-xs text-gray-400 leading-relaxed">
                  {calculatedQuote.representative_example}
                </p>
              )}
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => setDialogTab('apply')}
              >
                Apply for Finance <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </TabsContent>
            
            {/* Application Tab */}
            <TabsContent value="apply" className="space-y-4 mt-4">
              {calculatedQuote && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <span className="font-bold">{currencySymbol} {calculatedQuote.monthly_payment.toLocaleString()}/month</span> for {calcInputs.tenure_months} months
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={applicationForm.name}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={applicationForm.email}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={applicationForm.phone}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+94 XX XXX XXXX"
                  />
                </div>
                
                <div>
                  <Label htmlFor="employment">Employment Type</Label>
                  <Select
                    value={applicationForm.employment_type}
                    onValueChange={(v) => setApplicationForm(prev => ({ ...prev, employment_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salaried">Salaried Employee</SelectItem>
                      <SelectItem value="self_employed">Self Employed</SelectItem>
                      <SelectItem value="business_owner">Business Owner</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="income">Monthly Income (optional)</Label>
                  <Input
                    id="income"
                    type="number"
                    value={applicationForm.monthly_income}
                    onChange={(e) => setApplicationForm(prev => ({ ...prev, monthly_income: e.target.value }))}
                    placeholder="50000"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setDialogTab('calculator')}
                >
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSubmitApplication}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Submit Application
                </Button>
              </div>
              
              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to be contacted by {selectedProvider?.name} regarding your finance application.
              </p>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PayMonthlyProviders;
